import { useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Dropdown,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Space,
  Switch,
  Table,
  message as toast,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CheckOutlined, MoreOutlined, PlusOutlined, SnippetsOutlined } from '@ant-design/icons';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { colors } from 'theme/tokens';
import { useFeed } from './shared/context';
import { HighlightedText, Pill } from './shared/components';
import { WORD_SEVERITY_HINT, WORD_SEVERITY_STYLE } from './shared/labels';
import { BannedWordRow, TestTextResult, WordSeverity } from './shared/types';
import { errorCode, feedError, fmtDate, normalizeTerm, riskyAsciiTerm, useFeedList } from './shared/utils';
import styles from './styles.module.scss';

const PAGE_SIZE = 50;
const SEVERITIES: WordSeverity[] = ['BLOCK', 'REVIEW', 'ALLOW'];

const SeverityPill = ({ severity }: { severity: WordSeverity }) => {
  const s = WORD_SEVERITY_STYLE[severity];
  if (!s) return <span>{severity}</span>;
  return (
    <Pill color={s.color} bg={s.bg}>
      {s.label}
    </Pill>
  );
};

const RISKY_TEXT = '“cặc” bỏ dấu thành “các” — từ một chữ có dấu mà bắt cả khi gõ không dấu sẽ bắt nhầm từ thường.';

interface EditState {
  row: BannedWordRow | null; // null = thêm mới
}

/** Hộp "Thêm từ" / "Sửa từ" (D24 / D25). */
const WordModal = ({
  state,
  onClose,
  onSaved,
  onExists,
}: {
  state: EditState | null;
  onClose: () => void;
  onSaved: () => void;
  onExists: (term: string) => void;
}) => {
  const editing = !!state?.row;
  const [term, setTerm] = useState(state?.row?.term || '');
  const [severity, setSeverity] = useState<WordSeverity>(state?.row?.severity || 'BLOCK');
  const [asciiOk, setAsciiOk] = useState(!!state?.row?.asciiOk);
  const [note, setNote] = useState(state?.row?.note || '');
  const [saving, setSaving] = useState(false);

  if (!state) return null;
  const normalized = normalizeTerm(term);
  const valid = !!normalized && normalized.length <= 100;

  const submit = async () => {
    if (!valid) return;
    setSaving(true);
    try {
      if (state.row) {
        await AxiosUtils.patch(`${API.feedAdminBannedWords}/${state.row.id}`, {
          severity,
          asciiOk,
          note: note.trim() || null,
        });
        toast.success(`Đã lưu từ “${state.row.term}”.`);
      } else {
        await AxiosUtils.post(API.feedAdminBannedWords, {
          term: normalized,
          severity,
          asciiOk,
          note: note.trim() || undefined,
        });
        toast.success(`Đã thêm từ “${normalized}”.`);
      }
      onClose();
      onSaved();
    } catch (err) {
      if (errorCode(err) === 'FEED_WORD_EXISTS') {
        toast.error('Từ này đã có trong danh sách.');
        onClose();
        onExists(normalized);
      } else {
        toast.error(feedError(err));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      width={560}
      title={editing ? `Sửa từ “${state.row?.term}”` : 'Thêm từ cấm'}
      onCancel={onClose}
      onOk={submit}
      okText={editing ? 'Lưu' : 'Thêm'}
      cancelText="Huỷ"
      okButtonProps={{ disabled: !valid }}
      confirmLoading={saving}
      destroyOnHidden
    >
      <Form layout="vertical">
        <Form.Item
          label="Từ"
          required
          extra={
            editing
              ? 'Không đổi được chữ của từ — muốn đổi thì xoá rồi thêm lại.'
              : normalized && normalized !== term
              ? `Sẽ lưu dạng chuẩn hoá: “${normalized}”`
              : 'Chữ thường, có dấu. Cụm nhiều chữ cách nhau một dấu cách.'
          }
        >
          <Input
            className={styles.words}
            value={term}
            disabled={editing}
            maxLength={100}
            onChange={(e) => setTerm(e.target.value)}
          />
        </Form.Item>
        <Form.Item label="Mức">
          <Radio.Group value={severity} onChange={(e) => setSeverity(e.target.value)}>
            <Space direction="vertical" size={4}>
              {SEVERITIES.map((s) => (
                <Radio key={s} value={s}>
                  {WORD_SEVERITY_HINT[s]}
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </Form.Item>
        <Form.Item>
          <Checkbox checked={asciiOk} onChange={(e) => setAsciiOk(e.target.checked)}>
            Bắt cả khi gõ KHÔNG DẤU
          </Checkbox>
          {asciiOk && riskyAsciiTerm(term || state.row?.term || '') && (
            <Alert type="warning" showIcon style={{ marginTop: 8 }} message={RISKY_TEXT} />
          )}
        </Form.Item>
        <Form.Item label="Ghi chú">
          <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={255} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

/** Hộp "Dán danh sách" (D27). */
const ImportModal = ({ open, onClose, onSaved }: { open: boolean; onClose: () => void; onSaved: () => void }) => {
  const [text, setText] = useState('');
  const [severity, setSeverity] = useState<WordSeverity>('BLOCK');
  const [asciiOk, setAsciiOk] = useState(false);
  const [saving, setSaving] = useState(false);
  const lines = text.split(/\r?\n/).filter((l) => l.trim() && !l.trim().startsWith('#')).length;

  const submit = async () => {
    setSaving(true);
    try {
      const res: any = await AxiosUtils.post(API.feedAdminBannedWordsImport, { text, severity, asciiOk });
      const added = res?.data?.added ?? 0;
      const skipped = res?.data?.skipped ?? 0;
      toast.success(`Đã thêm ${added}, bỏ qua ${skipped} (đã có).`);
      setText('');
      onClose();
      onSaved();
    } catch (err) {
      toast.error(feedError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      width={640}
      title="Dán danh sách từ"
      onCancel={onClose}
      onOk={submit}
      okText={lines ? `Thêm ${lines} dòng` : 'Thêm'}
      cancelText="Huỷ"
      okButtonProps={{ disabled: !lines || text.length > 50000 }}
      confirmLoading={saving}
      destroyOnHidden
    >
      <Form layout="vertical">
        <Form.Item
          label="Mỗi dòng một từ"
          extra="Dòng bắt đầu bằng # là ghi chú. Từ đã có trong danh sách sẽ được bỏ qua."
        >
          <Input.TextArea
            className={styles.words}
            value={text}
            onChange={(e) => setText(e.target.value)}
            autoSize={{ minRows: 8, maxRows: 16 }}
            maxLength={50000}
            showCount
          />
        </Form.Item>
        <Form.Item label="Mức (áp cho cả lô)">
          <Radio.Group value={severity} onChange={(e) => setSeverity(e.target.value)}>
            {SEVERITIES.map((s) => (
              <Radio key={s} value={s}>
                {WORD_SEVERITY_STYLE[s].label}
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>
        <Checkbox checked={asciiOk} onChange={(e) => setAsciiOk(e.target.checked)}>
          Bắt cả khi gõ KHÔNG DẤU (áp cho cả lô)
        </Checkbox>
        <div className={styles.hint} style={{ marginTop: 12 }}>
          Có thể dán danh sách mở blue-eyes-vn/vietnamese-offensive-words (giấy phép MIT). Nhớ bỏ các từ một chữ
          trùng từ thường như “buoi”.
        </div>
      </Form>
    </Modal>
  );
};

/** Ô "Thử câu" (D28) — ADMIN và MOD. */
const TestSentence = () => {
  const [text, setText] = useState('');
  const [result, setResult] = useState<{ text: string; value: TestTextResult } | null>(null);
  const [loading, setLoading] = useState(false);
  const run = async () => {
    if (!text.trim()) return;
    setLoading(true);
    try {
      const res: any = await AxiosUtils.post(API.feedAdminTestText, { text });
      setResult({ text, value: res?.data as TestTextResult });
    } catch (err) {
      toast.error(feedError(err));
    } finally {
      setLoading(false);
    }
  };
  const verdict = result?.value?.verdict;
  return (
    <div className={styles.box} style={{ marginTop: 16 }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>Thử câu</div>
      <Space.Compact style={{ width: '100%' }}>
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onPressEnter={run}
          maxLength={5000}
          placeholder="Gõ một câu để xem máy bắt từ nào…"
        />
        <Button type="primary" loading={loading} onClick={run}>
          Thử
        </Button>
      </Space.Compact>
      {result && (
        <div className={styles.testResult}>
          <Space size={8} wrap style={{ marginBottom: 4 }}>
            <span>Kết quả:</span>
            {verdict === 'block' ? (
              <SeverityPill severity="BLOCK" />
            ) : verdict === 'review' ? (
              <SeverityPill severity="REVIEW" />
            ) : (
              <Pill color={colors.successInk} bg={colors.successSoft}>
                Sạch
              </Pill>
            )}
          </Space>
          <div>
            <HighlightedText text={result.text} matches={result.value?.matches || []} />
          </div>
        </div>
      )}
      <div className={styles.hint} style={{ marginTop: 6 }}>
        Thử câu dùng danh sách ĐÃ LƯU trên máy chủ.
      </div>
    </div>
  );
};

/** Phần "Từ cấm" GĐ1 (03 §11.6): bảng CRUD + dán hàng loạt + thử câu. Bộ 3 ô so khác biệt: GĐ2. */
const WordsCard = () => {
  const { isAdmin } = useFeed();
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [severity, setSeverity] = useState<WordSeverity | undefined>();
  const [active, setActive] = useState<string>('true');
  const [page, setPage] = useState(1);
  const [tick, setTick] = useState(0);
  const [editing, setEditing] = useState<EditState | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const reload = () => setTick((t) => t + 1);

  const { items, total, loading } = useFeedList<BannedWordRow>(
    API.feedAdminBannedWords,
    { q, severity, active: active === 'all' ? undefined : active, page, limit: PAGE_SIZE },
    { refreshKey: tick }
  );

  const toggle = async (row: BannedWordRow, isActive: boolean) => {
    try {
      await AxiosUtils.patch(`${API.feedAdminBannedWords}/${row.id}`, { isActive });
      toast.success(isActive ? `Đã bật từ “${row.term}”.` : `Đã tắt từ “${row.term}”.`);
      reload();
    } catch (err) {
      toast.error(feedError(err));
    }
  };

  const remove = (row: BannedWordRow) => {
    Modal.confirm({
      title: `Xoá từ “${row.term}”?`,
      content: 'Xoá hẳn khỏi danh sách (Nhật ký vẫn ghi lại). Muốn tạm ngưng thì dùng công tắc Bật.',
      okText: 'Xoá',
      okButtonProps: { danger: true },
      cancelText: 'Huỷ',
      onOk: async () => {
        try {
          await AxiosUtils._delete(`${API.feedAdminBannedWords}/${row.id}`, undefined);
          toast.success(`Đã xoá từ “${row.term}”.`);
          reload();
        } catch (err) {
          toast.error(feedError(err));
        }
      },
    });
  };

  const columns: ColumnsType<BannedWordRow> = [
    {
      title: 'Từ',
      key: 'term',
      width: 200,
      render: (_, r) => (
        <span className={styles.words} style={{ color: r.isActive ? undefined : colors.textMuted }}>
          {r.term}
        </span>
      ),
    },
    { title: 'Mức', key: 'severity', width: 120, render: (_, r) => <SeverityPill severity={r.severity} /> },
    {
      title: 'Không dấu',
      key: 'asciiOk',
      width: 96,
      align: 'center',
      render: (_, r) => (r.asciiOk ? <CheckOutlined style={{ color: colors.successInk }} /> : null),
    },
    {
      title: 'Bật',
      key: 'isActive',
      width: 72,
      render: (_, r) =>
        isAdmin ? (
          <Switch size="small" checked={r.isActive} onChange={(v) => toggle(r, v)} />
        ) : (
          <span style={{ fontSize: 13 }}>{r.isActive ? 'Bật' : 'Tắt'}</span>
        ),
    },
    {
      title: 'Ghi chú',
      key: 'note',
      render: (_, r) => <span style={{ color: r.note ? undefined : colors.textMuted }}>{r.note || '—'}</span>,
    },
    {
      // 104 px (03 §11.6) làm "29/09/2026" xuống 2 dòng với Be Vietnam Pro → 120 + không xuống dòng.
      title: 'Sửa lúc',
      key: 'updatedAt',
      width: 120,
      render: (_, r) => <span style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.updatedAt || r.createdAt)}</span>,
    },
  ];
  if (isAdmin) {
    columns.push({
      title: '',
      key: 'actions',
      width: 56,
      fixed: 'right',
      render: (_, r) => (
        <Dropdown
          trigger={['click']}
          menu={{
            items: [
              { key: 'edit', label: 'Sửa' },
              { key: 'delete', label: 'Xoá', danger: true },
            ],
            onClick: ({ key }) => (key === 'edit' ? setEditing({ row: r }) : remove(r)),
          }}
        >
          <Button size="small" icon={<MoreOutlined />} aria-label="Thao tác" />
        </Dropdown>
      ),
    });
  }

  return (
    <div>
      <div className={styles.toolbar}>
        <h3 className={styles.sectionTitle} style={{ margin: 0 }}>
          Từ cấm
        </h3>
        {isAdmin && (
          <Space wrap>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditing({ row: null })}>
              Thêm từ
            </Button>
            <Button icon={<SnippetsOutlined />} onClick={() => setImportOpen(true)}>
              Dán danh sách
            </Button>
          </Space>
        )}
      </div>
      <div className={styles.hint} style={{ marginBottom: 12 }}>
        Từ cấm luôn chạy khi đăng bài và bình luận, không cần AI. “Chặn hẳn” không cho đăng và chỉ ra đúng từ đó;
        “Cần xem” vẫn đăng nhưng vào tab Cần xem; “Ngoại lệ” không bao giờ bắt (vd. “óc chó” là hạt óc chó).
      </div>
      <div className={styles.filters}>
        <Input.Search
          allowClear
          placeholder="Tìm từ"
          style={{ width: 200 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onSearch={(v) => {
            setQ(v.trim());
            setPage(1);
          }}
        />
        <Select
          allowClear
          placeholder="Mức: Tất cả"
          style={{ width: 160 }}
          value={severity}
          options={SEVERITIES.map((s) => ({ value: s, label: WORD_SEVERITY_STYLE[s].label }))}
          onChange={(v) => {
            setSeverity(v);
            setPage(1);
          }}
        />
        <Select
          style={{ width: 160 }}
          value={active}
          options={[
            { value: 'true', label: 'Đang bật' },
            { value: 'false', label: 'Đang tắt' },
            { value: 'all', label: 'Mọi trạng thái' },
          ]}
          onChange={(v) => {
            setActive(v);
            setPage(1);
          }}
        />
      </div>
      <div className="custom-table">
        <Table<BannedWordRow>
          rowKey="id"
          size="middle"
          loading={loading}
          columns={columns}
          dataSource={items}
          locale={{ emptyText: 'Chưa có từ nào. Bảng rỗng thì mọi nội dung đều qua (không phải lỗi).' }}
          scroll={items.length ? { x: 864 } : undefined}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total,
            showSizeChanger: false,
            hideOnSinglePage: true,
            onChange: setPage,
          }}
        />
      </div>
      <TestSentence />
      {editing && (
        <WordModal
          key={editing.row?.id ?? 'new'}
          state={editing}
          onClose={() => setEditing(null)}
          onSaved={reload}
          onExists={(term) => {
            setSearch(term);
            setQ(term);
            setActive('all');
            setPage(1);
          }}
        />
      )}
      <ImportModal open={importOpen} onClose={() => setImportOpen(false)} onSaved={reload} />
    </div>
  );
};

export default WordsCard;
